import type { CmsSignpost, SearchAllTypesDto } from '@gov-nx/api';
import type { Nullable } from '@gov-nx/core/types';
import { compareProperty, is } from '@gov-nx/core/types';
import type {
	SearchAllActionParams,
	SearchAllTypesResult,
} from '@gov-nx/module/page';
import { getPageUrlByCode, PageCode } from '@gov-nx/module/page';
import type { ServiceCode } from '@gov-nx/module/service';
import { getServiceUrlByCode } from '@gov-nx/module/service';
import { pageRouteResolver } from '@gov-nx/web';
import { MIN_SEARCH_TERM_LENGTH } from './constants';

export const searchResultsComparator =
	(sortAlphabetically: boolean) =>
	(a: SearchAllTypesResult, b: SearchAllTypesResult) => {
		if (sortAlphabetically) return compareProperty('titleForSorting')(a, b);
		return 0;
	};

export type SearchResultLink = {
	to: string | undefined;
	external?: boolean;
	onAfterClick?: () => void;
};

export const resolveLinkFromSearchAllTypes = (
	item: SearchAllTypesDto
): SearchResultLink => {
	if (item.signpost) {
		const { pageCode, serviceCode, externalServiceId } = item.signpost;
		if (pageCode) {
			return {
				to: getPageUrlByCode(pageCode as PageCode) ?? '#',
			};
		} else if (serviceCode) {
			return {
				to: getServiceUrlByCode(serviceCode as ServiceCode) ?? '#',
			};
		} else if (externalServiceId) {
			return { to: undefined };
		}
	} else if (item.usefulLink) {
		return { to: item?.usefulLink.link, external: true };
	} else if (item.faq) {
		return {
			to: pageRouteResolver(PageCode['casto-se-ptate'], {
				slug: item.faq.slug,
			}),
		};
	}

	return { to: undefined };
};

export const resolveLinkFromCmsSignpost = (
	item: CmsSignpost
): SearchResultLink => {
	if (item.pageCode) {
		return {
			to: getPageUrlByCode(item.pageCode as PageCode) ?? '#',
		};
	} else if (item.serviceCode) {
		return {
			to: getServiceUrlByCode(item.serviceCode as ServiceCode) ?? '#',
		};
	} else if (item.externalServiceId) {
		return { to: undefined };
	}
	return { to: undefined };
};

export const resolveLinkFromActionParam = (
	actionParam: SearchAllActionParams
): SearchResultLink => {
	if (actionParam.pageCode) {
		return {
			to: getPageUrlByCode(actionParam.pageCode as PageCode) ?? '#',
		};
	} else if (actionParam.serviceCode) {
		return {
			to: getServiceUrlByCode(actionParam.serviceCode as ServiceCode) ?? '#',
		};
	} else if (actionParam.externalServiceId) {
		return { to: undefined };
	}
	return { to: undefined };
};

export const getTagLabel = (type: string, t: (key: string) => string) => {
	switch (type) {
		case 'faq':
			return t('tag.caste-dotazy');
		case 'usefulLink':
			return t('tag.uzitecne-odkazy');
		default:
			return t('tag.sluzby');
	}
};

export const isSearchValid = (searchTerm: Nullable<string> | undefined) => {
	if (is(searchTerm)) {
		return {
			isSearchValue: searchTerm.length >= MIN_SEARCH_TERM_LENGTH,
			validSearchValue: searchTerm,
		};
	}

	return {
		isSearchValue: false,
	};
};
